import { initReactI18next } from 'react-i18next';
import * as Sentry from '@sentry/browser';
import i18n from 'i18next';
import backend from 'i18next-http-backend';

interface Api {
  url: string;
  context: {
    country: string;
    merchantId: string;
    userId: string;
    sessionId: string;
    acceptLanguage: string;
  };
}

const loadTranslations = (
  apiUrl: string,
  merchantId: string,
  userId: string,
  sessionId: string,
  country: string,
  language: string,
) => {
  const token = btoa(`${merchantId}:${userId}:${sessionId}`);

  Sentry.addBreadcrumb({
    category: 'i18n.translations',
    level: 'error',
    message: 'Translations request started',
    data: {
      requestUrl: `${apiUrl}/api/v1/cashier/translations?country=${country}`,
      country,
      language,
      merchantId,
      userId,
      sessionId,
      token,
    },
  });

  return fetch(`${apiUrl}/api/v1/cashier/translations?country=${country}`, {
    headers: {
      'Pgw-Authorization': token,
      'Accept-Language': language,
    },
  });
};

const initI18n = (api: Api, onTranslationsLoaded: () => void): void => {
  const {
    url: apiUrl,
    context: { country, merchantId, userId, sessionId, acceptLanguage },
  } = api;

  if (!merchantId || !userId || !sessionId) {
    return;
  }

  const backendOptions = {
    loadPath: '{{lng}}|{{ns}}',
    request: (_: any, url: any, __: any, callback: any) => {
      try {
        const [lng] = url.split('|');

        loadTranslations(apiUrl, merchantId, userId, sessionId, country, lng)
          .then((resp) => {
            Sentry.addBreadcrumb({
              category: 'i18n.translations',
              level: 'error',
              message: 'Translations response received',
              data: {
                status: resp.status,
                ok: resp.ok,
                statusText: resp.statusText,
                resp,
              },
            });

            if (!resp.ok) {
              throw new Error(
                `Failed to load translations: ${resp.status} ${resp.statusText}`,
              );
            }

            const contentType = resp.headers.get('Content-Type') || '';
            const isJson = contentType.includes('application/json');

            if (!isJson) {
              Sentry.addBreadcrumb({
                category: 'i18n.translations',
                level: 'error',
                message: 'Translations response is not JSON',
                data: { contentType },
              });
            }

            const responseBody = isJson ? resp.json() : Promise.resolve(resp);

            return responseBody.then((json) => {
              Sentry.addBreadcrumb({
                category: 'i18n.translations',
                level: 'error',
                message: 'Translations response body parsed',
                data: {
                  json,
                },
              });
              return json;
            });
          })
          .then((resp) => {
            Sentry.addBreadcrumb({
              category: 'i18n.translations',
              level: 'error',
              message: 'Translations loaded successfully',
              data: {
                resp,
              },
            });

            if (!resp.data) {
              throw new Error('Invalid translation response format');
            }

            callback(null, {
              data: resp.data,
              status: 200,
            });
          })
          .catch((error) => {
            callback(null, {
              data: [],
              status: 200,
            });

            Sentry.captureException(
              new Error('####Fetch translation error. Error####: '),
              {
                extra: {
                  error,
                  apiUrl,
                  requestUrl: `${apiUrl}/api/v1/cashier/translations?country=${country}`,
                  country,
                  lng,
                  merchantId,
                  userId,
                  sessionId,
                },
              },
            );
          });
      } catch (error) {
        Sentry.captureException(
          new Error('####Translation loading failed. Error####: '),
          {
            extra: {
              error,
            },
          },
        );

        callback(null, {
          data: [],
          status: 200,
        });
      }
    },
  };

  i18n.on('loaded', onTranslationsLoaded);

  i18n
    .use(backend)
    .use(initReactI18next)
    .init({
      lng: acceptLanguage,
      fallbackLng: acceptLanguage,
      load: 'currentOnly',
      keySeparator: false,
      backend: backendOptions,
      debug: false,
      interpolation: {
        escapeValue: false,
        formatSeparator: ',',
      },
    });
};

export { i18n, initI18n };
